import type { DateRange } from 'react-day-picker';

import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
  type Locale,
} from 'date-fns';

export const selectButtons = [
  'yesterday',
  'thisWeek',
  'lastWeek',
  'thisMonth',
  'lastMonth',
  'thisQuarter',
  'lastQuarter',
  'thisYear',
  'lastYear',
  'allTime',
] as const;

export type SelectButtonType = (typeof selectButtons)[number];

export const getDateRangeBySelectButton = (
  button: SelectButtonType,
  localize: Locale,
  allTimeButton?: {
    from?: Date;
    to?: Date;
  },
): DateRange & { selectMonthDate?: Date } => {
  const today = new Date();
  const yesterday = subDays(today, 1);
  const fromWeek = startOfWeek(today, localize.options);
  const toWeek = endOfWeek(today, localize.options);
  const previousWeekDay = subWeeks(today, 1);
  const fromLastWeek = startOfWeek(previousWeekDay, localize.options);
  const toLastWeek = endOfWeek(previousWeekDay, localize.options);
  const startMonth = startOfMonth(today);
  const endMonth = endOfMonth(today);
  const previousMonthDay = subMonths(today, 1);
  const startLastMonth = startOfMonth(previousMonthDay);
  const endLastMonth = endOfMonth(previousMonthDay);
  const startQuarter = startOfQuarter(today);
  const endQuarter = endOfQuarter(today);
  const previousQuarterDay = subQuarters(today, 1);
  const startLastQuarter = startOfQuarter(previousQuarterDay);
  const endQLastuarter = endOfQuarter(previousQuarterDay);
  const startYear = startOfYear(today);
  const endYear = endOfYear(today);
  const previousYearDay = subYears(today, 1);
  const startLastYear = startOfYear(previousYearDay);
  const endLastYear = endOfYear(previousYearDay);
  switch (button) {
    case 'yesterday':
      return { from: yesterday, to: yesterday, selectMonthDate: yesterday };
    case 'thisWeek':
      return { from: fromWeek, to: toWeek, selectMonthDate: fromWeek };
    case 'lastWeek':
      return {
        from: fromLastWeek,
        to: toLastWeek,
        selectMonthDate: previousWeekDay,
      };
    case 'thisMonth':
      return { from: startMonth, to: endMonth, selectMonthDate: startMonth };
    case 'lastMonth':
      return {
        from: startLastMonth,
        to: endLastMonth,
        selectMonthDate: startLastMonth,
      };
    case 'thisQuarter':
      return { from: startQuarter, to: endQuarter, selectMonthDate: today };
    case 'lastQuarter':
      return {
        from: startLastQuarter,
        to: endQLastuarter,
        selectMonthDate: startLastQuarter,
      };
    case 'thisYear':
      return { from: startYear, to: endYear, selectMonthDate: today };
    case 'lastYear':
      return {
        from: startLastYear,
        to: endLastYear,
        selectMonthDate: startLastYear,
      };
    case 'allTime':
    default:
      return {
        from: allTimeButton?.from,
        to: allTimeButton?.to,
        selectMonthDate: allTimeButton?.from,
      };
  }
};
